import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../chrome/AppShell';
import useUserOrg from '../../../Authentication/components/hooks/useUserOrg';
import useDeviceKeys from '../../../DeviceManagement/components/hooks/useDeviceKeys';
import { Crumb, Field, SectionCard, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import './devices.css';

/*
 * New device key. The server generates the pair; the secret is only
 * ever shown on this page and in the downloaded config file.
 */

interface KeyPair {
    keyId: string;
    secretAccessKey: string;
}

function download(values: { name: string } & KeyPair) {
    const a = document.createElement('a');
    const url = URL.createObjectURL(
        new Blob([JSON.stringify(values)], { type: 'text/plain' }),
    );
    a.href = url;
    a.download = `${values.name}_device_key.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // revoking synchronously can cancel the download in some browsers
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function CopyValue({
    label,
    value,
    masked,
    onCopied,
}: {
    label: string;
    value: string;
    masked?: boolean;
    onCopied: (ok: boolean) => void;
}) {
    const [shown, setShown] = useState(!masked);
    return (
        <div className="field" style={{ maxWidth: '100%' }}>
            <span className="field-l">{label}</span>
            <div className="dv-code">
                <code aria-label={label}>
                    {shown ? value : '•'.repeat(Math.min(value.length, 32))}
                </code>
                {masked && (
                    <button
                        type="button"
                        className="tb-btn"
                        aria-pressed={shown}
                        onClick={() => setShown((v) => !v)}
                    >
                        {shown ? 'Hide' : 'Show'}
                    </button>
                )}
                <button
                    type="button"
                    className="tb-btn"
                    aria-label={`Copy ${label.toLowerCase()}`}
                    onClick={() => {
                        const p = navigator.clipboard?.writeText(value);
                        if (!p) return onCopied(false);
                        p.then(() => onCopied(true)).catch(() => onCopied(false));
                    }}
                >
                    Copy
                </button>
            </div>
        </div>
    );
}

export default function DeviceKeyCreateV3() {
    const navigate = useNavigate();
    const { groupId } = useParams();
    const { orgId } = useUserOrg();
    const { generateDeviceKey, addDeviceKey } = useDeviceKeys(orgId);
    const toast = useToast();

    const [pair, setPair] = useState<KeyPair | null>(null);
    const [genFailed, setGenFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [created, setCreated] = useState<({ name: string } & KeyPair) | null>(null);
    const busy = useRef(false);

    useEffect(() => {
        if (!orgId) return;
        let live = true;
        setGenFailed(false);
        generateDeviceKey()
            .then((p: KeyPair) => {
                if (!live) return;
                if (p?.keyId && p?.secretAccessKey) setPair(p);
                else setGenFailed(true);
            })
            .catch(() => live && setGenFailed(true));
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, attempt]);

    const back = groupId
        ? {
              label: 'Group',
              to: `/device-management/device-groups/${groupId}/device-keys`,
          }
        : { label: 'Devices', to: '/device-management#keys' };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!pair || busy.current) return;
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError('Give the device a name.');
            return;
        }
        busy.current = true;
        setSaving(true);
        setSaveError('');
        const values = {
            name: trimmed,
            keyId: pair.keyId,
            secretAccessKey: pair.secretAccessKey,
        };
        try {
            const ok = await addDeviceKey(values);
            if (!ok) throw new Error('not saved');
            download(values);
            setCreated(values);
        } catch {
            setSaveError('Couldn’t create the key. Try again.');
        } finally {
            busy.current = false;
            setSaving(false);
        }
    };

    const copied = (ok: boolean) =>
        toast.show(ok ? 'Copied' : 'Couldn’t copy — select it and copy by hand.');

    return (
        <AppShell>
            <div className="dv-page dets">
                <Crumb label={back.label} onClick={() => navigate(back.to)} />
                <PageHeadActions
                    title={created ? 'Device key created' : 'New device key'}
                    sub={
                        created
                            ? `${created.name} can now sign in with this key.`
                            : 'A key lets one device upload to this org.'
                    }
                />

                {genFailed && !pair && (
                    <TCard>
                        <TEmpty
                            message="Couldn’t generate a key pair."
                            actionLabel="Retry"
                            onAction={() => setAttempt((n) => n + 1)}
                        />
                    </TCard>
                )}
                {!genFailed && !pair && (
                    <TCard>
                        <TSkeleton rows={3} />
                    </TCard>
                )}

                {pair && !created && (
                    <form onSubmit={submit} noValidate>
                        <div className="stack">
                            <SectionCard title="Device">
                                <Field
                                    label="Device name"
                                    help="Shown on the device list and assignments."
                                    error={nameError}
                                >
                                    <TextInput
                                        autoFocus
                                        value={name}
                                        disabled={saving}
                                        aria-invalid={!!nameError}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            if (nameError) setNameError('');
                                        }}
                                    />
                                </Field>
                            </SectionCard>
                            <SectionCard title="Credentials">
                                <p className="banner dv-note warn" role="note">
                                    You won’t see this secret again after you
                                    leave this page. Creating the key also
                                    downloads it as a config file.
                                </p>
                                <CopyValue label="Key ID" value={pair.keyId} onCopied={copied} />
                                <div style={{ height: 14 }} />
                                <CopyValue
                                    label="Secret access key"
                                    value={pair.secretAccessKey}
                                    masked
                                    onCopied={copied}
                                />
                            </SectionCard>
                        </div>
                        {saveError && (
                            <p className="banner dv-note err" role="alert" style={{ marginTop: 16 }}>
                                {saveError}
                            </p>
                        )}
                        <div className="dv-actions">
                            <button
                                type="button"
                                className="tb-btn"
                                disabled={saving}
                                onClick={() => navigate(back.to)}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="tb-new" disabled={saving}>
                                {saving ? 'Creating…' : 'Create key'}
                            </button>
                        </div>
                    </form>
                )}

                {created && (
                    <>
                        <SectionCard title="Credentials">
                            <p className="banner dv-note warn" role="note">
                                Copy the secret now — you won’t see it again.
                                The config file{' '}
                                <span className="dv-mono">{`${created.name}_device_key.json`}</span>{' '}
                                was downloaded too.
                            </p>
                            <CopyValue label="Key ID" value={created.keyId} onCopied={copied} />
                            <div style={{ height: 14 }} />
                            <CopyValue
                                label="Secret access key"
                                value={created.secretAccessKey}
                                masked
                                onCopied={copied}
                            />
                        </SectionCard>
                        <div className="dv-actions">
                            <button type="button" className="tb-btn" onClick={() => download(created)}>
                                Download again
                            </button>
                            <button
                                type="button"
                                className="tb-new"
                                onClick={() => navigate('/device-management#keys')}
                            >
                                Done
                            </button>
                        </div>
                    </>
                )}
            </div>
            {toast.node}
        </AppShell>
    );
}
